/**
 * Lightweight wrapper around `navigator.geolocation.getCurrentPosition`
 * that fits the way the Clock In / Clock Out flow needs to use it:
 *
 *   - One-shot capture only — never starts a watchPosition.
 *   - Honours a short timeout so a stuck GPS lock doesn't block the user.
 *   - Always resolves (never rejects): if permission is denied, the device
 *     has no GPS, the user is on http (not https), or it simply times out,
 *     the helper returns `null`. The caller treats that as
 *     "Location not provided" and continues the clock event so the
 *     business flow is never blocked by GPS issues.
 *   - Cached consent: once the user accepts the one-time pre-prompt
 *     notice we set a localStorage flag so we don't show the dialog on
 *     every clock event. The browser still owns the underlying
 *     permission state; we only suppress the in-app notice.
 */
const CONSENT_KEY = 'san_loc_consent_v1';

export function hasShownLocationNotice() {
  try {
    return localStorage.getItem(CONSENT_KEY) === '1';
  } catch {
    return false;
  }
}

export function markLocationNoticeShown() {
  try {
    localStorage.setItem(CONSENT_KEY, '1');
  } catch {
    /* private mode / SSR — harmless to skip */
  }
}

export function captureLocation({ timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!pos || !pos.coords) {
            done(null);
            return;
          }
          done({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy ?? null,
          });
        },
        () => done(null),
        {
          enableHighAccuracy: true,
          timeout: timeoutMs,
          maximumAge: 0,
        }
      );
    } catch {
      done(null);
    }
    // Safety net — even if the browser never fires either callback we
    // unblock the UI after the requested timeout.
    setTimeout(() => done(null), timeoutMs + 500);
  });
}

/**
 * STRICT capture used by employee Clock In (GPS is mandatory there).
 * Resolves { loc, status } where status is one of:
 *   'ok' | 'denied' | 'unavailable' | 'timeout' | 'unsupported'
 * `loc` is null unless status === 'ok'.
 */
export function captureLocationDetailed({ timeoutMs = 12000 } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ loc: null, status: 'unsupported' });
      return;
    }
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!pos || !pos.coords) {
            done({ loc: null, status: 'unavailable' });
            return;
          }
          done({
            loc: {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy: pos.coords.accuracy ?? null,
            },
            status: 'ok',
          });
        },
        (err) => {
          const status = err && err.code === 1 ? 'denied' : (err && err.code === 3 ? 'timeout' : 'unavailable');
          done({ loc: null, status });
        },
        {
          enableHighAccuracy: true,
          timeout: timeoutMs,
          maximumAge: 0,
        }
      );
    } catch {
      done({ loc: null, status: 'unavailable' });
    }
    setTimeout(() => done({ loc: null, status: 'timeout' }), timeoutMs + 500);
  });
}

export function locationErrorMessage(status) {
  if (status === 'denied') {
    return 'Location access is blocked. GPS is required to clock in — enable location for this site in your browser/phone settings, then try again.';
  }
  if (status === 'unsupported') {
    return 'This device or browser does not support GPS location, which is required to clock in. Please use a device with location services.';
  }
  return 'Could not get your GPS location. Make sure location services are ON, move to an open area and try again — GPS is required to clock in.';
}
